import { observer } from "mobx-react-lite"

import type { Sort } from "@/domains/table-processor"
import type { ChainConfigToken } from "@/domains/core"
import { SortableTableHeader } from "@/components/sortable-table-header"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableHeaderRow, TableRow } from "@/components/table"
import { TextPercentage } from "@/components/text-percentage"
import { TokenIcon } from "@/components/token-icon"
import { cn, dollars, formatCurrency, formatPercentage } from "@/domains/library"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

import type { IRevenueSourceCard } from "./types"
import { DilutionRiskColumn } from "./dilution-risk-column"
import { VoteInputColumn } from "./vote-input-column"

interface RevenueSourceTableViewProps {
  revenueSources: IRevenueSourceCard[]
  totalUsed: number
  onUpdateAllocation: (id: string, value: number) => void
  governanceToken: ChainConfigToken
  sortBy: Sort
  onSortChange: (sort: Sort) => void
}

export const RevenueSourceTableView = observer(
  ({
    revenueSources,
    totalUsed,
    onUpdateAllocation,
    governanceToken,
    sortBy,
    onSortChange,
  }: RevenueSourceTableViewProps) => {
    return (
      <div className="overflow-x-auto overflow-y-hidden rounded-xl border border-p400-light">
        <Table className="w-full min-w-[1000px]">
          <TableHead>
            <TableHeaderRow>
              <SortableTableHeader
                className="pl-2"
                classNameInner="w-full justify-start"
                sortBy={sortBy}
                sortColumn="displayName"
                onClick={() =>
                  onSortChange({
                    key: "displayName",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "string",
                  })
                }
              >
                Pair
              </SortableTableHeader>
              <SortableTableHeader
                classNameInner="w-full justify-end"
                sortBy={sortBy}
                sortColumn="votes"
                onClick={() =>
                  onSortChange({
                    key: "votes",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }
              >
                Votes on
                <br />
                Pool
              </SortableTableHeader>
              <SortableTableHeader
                classNameInner="w-full justify-end"
                sortBy={sortBy}
                sortColumn="emissions"
                onClick={() =>
                  onSortChange({
                    key: "emissions",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }
              >
                Next <br />
                Emissions
              </SortableTableHeader>
              <SortableTableHeader
                classNameInner="w-full justify-end"
                sortBy={sortBy}
                sortColumn="apr"
                onClick={() =>
                  onSortChange({
                    key: "apr",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }
              >
                Voting <br />
                APR
              </SortableTableHeader>
              <SortableTableHeader
                classNameInner="w-full justify-end"
                sortBy={sortBy}
                sortColumn="fees"
                onClick={() =>
                  onSortChange({
                    key: "fees",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }
              >
                Fees and
                <br />
                Bribes
              </SortableTableHeader>
              <SortableTableHeader
                classNameInner="w-full justify-end"
                sortBy={sortBy}
                sortColumn="perVote"
                onClick={() =>
                  onSortChange({
                    key: "perVote",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }
              >
                My $ Per
                <br />
                Vote
              </SortableTableHeader>
              <SortableTableHeader
                className="pr-2"
                classNameInner="w-full justify-end"
                sortBy={sortBy}
                sortColumn="dilutionRisk"
                onClick={() =>
                  onSortChange({
                    key: "dilutionRisk",
                    direction: sortBy.direction === "asc" ? "desc" : "asc",
                    type: "number",
                  })
                }
              >
                Dilution Risk
              </SortableTableHeader>
              <TableHeader className="pr-4" classNameInner="w-full justify-end" isSortDisabled={true}>
                Vote
              </TableHeader>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {revenueSources.map((revenueSource) => (
              <TableRow
                key={revenueSource.id}
                className="border-b border-p400-light text-xs font-normal text-t100 sm:text-sm"
              >
                {/* Pair Column */}
                <TableCell className="max-w-[88px] px-2 py-[1.125rem] sm:max-w-[180px]">
                  <div className="flex flex-col items-start gap-2">
                    <DisplayTokens tokens={revenueSource.tokens} tokenSize={36} gap={2} overlap={6} />
                    <div className="text-left">
                      <div className="whitespace-nowrap text-t100">
                        {revenueSource.displayName ?? "Locating source..."}
                      </div>
                      <div className="text-xs text-t200">{revenueSource.protocol}</div>
                    </div>
                  </div>
                </TableCell>

                {/* Votes on Pool Column */}
                <TableCell className="px-2 py-[1.125rem] text-right sm:px-4">
                  <div className="flex flex-col items-end justify-center">
                    <p>{formatCurrency(revenueSource.votes, 0)}</p>
                    <p className="text-xs text-t200">{formatPercentage(revenueSource.votesPercent, 4)}</p>
                  </div>
                </TableCell>

                {/* Next Emissions Column */}
                <TableCell className="px-2 py-[1.125rem] text-right sm:px-4">
                  {revenueSource.emissions === 0 ? (
                    <div className="text-right">
                      <p className="text-xs text-t200">-</p>
                    </div>
                  ) : (
                    <div className="flex flex-row items-center justify-end gap-[3px]">
                      <p className="text-sm text-t200">{formatCurrency(revenueSource.emissions, 0)}</p>
                      <TokenIcon token={governanceToken} square={16} calledFrom="RevenueSourceTableView" />
                    </div>
                  )}
                </TableCell>

                {/* Voting APR Column */}
                <TableCell className="px-2 py-[1.125rem] text-right sm:px-4">
                  <TextPercentage value={revenueSource.apr} decimals={2} definition="" />
                </TableCell>

                {/* Fees and Bribes Column */}
                <TableCell className="px-2 py-[1.125rem] text-right sm:px-4">
                  <div className="flex justify-end">
                    <div className="flex w-full flex-col items-end justify-center gap-[6px] rounded-xl bg-secondary-400/[0.12] px-3 py-2">
                      <p className="text-sm">{dollars(revenueSource.fees, 2)}</p>
                      <div>
                        <div className="flex flex-col items-end gap-[4px]">
                          {revenueSource.incentives
                            .filter((incentive) => incentive.rewardAmountUsd > 0)
                            .map((incentive) => {
                              return (
                                <div
                                  key={incentive.token.address.toString()}
                                  className="flex items-center gap-1 text-xs text-secondary-400"
                                >
                                  <div className="text-secondary-400">{dollars(incentive.rewardAmountUsd, 2)}</div>
                                  <div className="text-secondary-600">{incentive.token.symbol}</div>
                                  <DisplayTokens tokens={[incentive.token]} tokenSize={14} />
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    </div>
                  </div>
                </TableCell>

                {/* My $ Per Vote Column */}
                <TableCell className="px-2 py-[1.125rem] text-right sm:px-4">
                  <div className="flex flex-col items-end justify-center">
                    <p className="font-bold text-green-400">${revenueSource.perVote.toFixed(6)}</p>
                    <p className="text-xs text-t200">{dollars(revenueSource.expectedIncome, 4)}</p>
                  </div>
                </TableCell>

                {/* Dilution Risk Column */}
                <TableCell className="px-2 py-[1.125rem] text-right sm:px-4">
                  <DilutionRiskColumn
                    dilutionRisk={revenueSource.dilutionRisk}
                    dilutionRiskRank={revenueSource.dilutionRiskRank}
                    dilutionRiskColor={revenueSource.dilutionRiskColor}
                  />
                </TableCell>

                {/* Vote Column */}
                <TableCell className="px-2 py-[1.125rem] sm:px-4">
                  <VoteInputColumn
                    revenueSource={revenueSource}
                    totalUsed={totalUsed}
                    onUpdateAllocation={onUpdateAllocation}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  },
)
